import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { WATodoCategoryPreview } from "./WidgetTodoOverviewPreview";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { useWidgetTodoOverviewPreviewQuery } from "./useWidgetTodoOverviewPreviewQuery";
import { WATodoOverviewMutation } from "./__generated__/WATodoOverviewMutation.graphql";
import { WATodoOverviewQuery } from "./__generated__/WATodoOverviewQuery.graphql";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetTodoOverviewSettings } from "../WidgetTodoOverviewSettings/WidgetTodoOverviewSettings";
import { WidgetScreen } from "../../common/WidgetScreen";

type Props = {
  route: UserLoggedInNavigatorRouteProp<"WidgetAddTodoCategory">;
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddTodoCategory">;
};

const WA_TODO_OVERVIEW_QUERY = graphql`
  query WATodoOverviewQuery {
    store {
      ...WidgetTodoOverviewSettings_Store
    }
  }
`;

export const WATodoCategory = withWebModal(WATodoCategoryC, true);
function WATodoCategoryC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWATodoOverview {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWATodoOverview(props: Props & { fetchKey: string }) {
  const [teamIds, setTeamIds] = useState(props.route.params.defaultTeams);
  const [displayMode, setDisplayMode] = useState<"MEDIUM" | "MEDIUM_GROUPED">(
    "MEDIUM"
  );

  const data = useLazyLoadQuery<WATodoOverviewQuery>(
    WA_TODO_OVERVIEW_QUERY,
    {},
    {
      fetchKey: props.fetchKey,
    }
  );

  const previewQuery = useWidgetTodoOverviewPreviewQuery({
    categoryId: props.route.params.categoryId,
    teamIds,
    storecastId: props.route.params.storecastId,
    displayMode,
  });

  const [createWidgetTodoCategory, loading] =
    useMutation<WATodoOverviewMutation>(graphql`
      mutation WATodoOverviewMutation(
        $storecastId: ID!
        $input: CreateWidgetTodoOverviewInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetTodoOverview(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidgetTodoCategory({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            categoryId: props.route.params.categoryId,
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
            teamIds,
            displayMode,
          },
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
        onError: (e) => {
          console.log("error: ", e);
        },
      });
    },
    [
      createWidgetTodoCategory,
      displayMode,
      props.navigation,
      props.route.params.categoryId,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
      teamIds,
    ]
  );

  const renderItem = useCallback(
    () =>
      previewQuery ? <WATodoCategoryPreview query={previewQuery} /> : null,
    [previewQuery]
  );

  return (
    <WidgetScreen
      preview={
        <WASizePreview
          unitSize={props.route.params.unitSize}
          onCreate={onCreate}
          loading={loading}
          maxSize={props.route.params.maxSize}
          widgetSizes={overviewWidgetSizes}
        >
          {renderItem}
        </WASizePreview>
      }
      settings={
        <WidgetTodoOverviewSettings
          store={data.store}
          setTeamIds={setTeamIds}
          teamIds={teamIds}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
        />
      }
    />
  );
}
